import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { checkAcceptedFileTypes, checkImageFile, checkPDF, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomFormLabel, ErrorMessage, FileErrorMessage, FileInputButton } from 'components';
import { Box, Typography, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    submit: {
        marginTop: 0,
        width: '45%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginTop: 16,
        },
    },
    label: {
        fontSize: 14,
        minWidth: 117,
    },
    fileName: {
        textAlign: 'left',
        maxWidth: '90%',
    },
    tableCellFileName: {
        maxWidth: 250,
    },
    fileInput: {
        margin: 0,
    },
    fileInputButton: {
        minWidth: 130,
        flex: '1 1 130px',
        margin: 'auto',
    },
    subtitle: {
        fontSize: 16,
        color: theme.palette.common.black,
        display: 'block',
        maxWidth: '100%',
        marginBottom: 20,
    },
}));

const DocumentUpload = ({
    selectedFiles,
    removeFile,
    ContainerProps,
    handleFileChange,
    label,
    inline,
    filesNamesInline,
    id,
    error,
    width = 'auto',
    isTableCell,
    acceptedFileTypes,
    type = 'all',
}) => {
    const classes = useStyles();
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [pdfDocError, setPdfDocError] = useState(false);

    const handleInterceptChange = useCallback(
        e => {
            const file = e.target?.files[0];

            // validate file size
            if (validateFileSize(file)) {
                setFileSizeError(false);
            } else {
                setFileSizeError(true);
            }

            // validate file type
            if (type === 'pdf') {
                const isPDF = checkPDF(file);
                if (isPDF) {
                    handleFileChange(e);
                    setPdfDocError(false);
                } else {
                    setPdfDocError(true);
                }
            } else if (type === 'image') {
                checkImageFile(
                    file,
                    () => {
                        setFileTypeError(false);
                        handleFileChange(e);
                    },
                    () => setFileTypeError(true)
                );
            } else {
                checkAcceptedFileTypes(
                    file,
                    () => {
                        if (validateFileSize(file)) {
                            setFileTypeError(false);
                            setFileSizeError(false);
                            handleFileChange(e);
                        } else {
                            setFileSizeError(true);
                        }
                    },
                    () => {
                        setFileSizeError(false);
                        setFileTypeError(true);
                    }
                );
            }
        },
        [handleFileChange, type]
    );

    const files = useMemo(() => {
        if (!selectedFiles) return [];
        return selectedFiles?.length ? selectedFiles.filter(f => !!f) : [selectedFiles];
    }, [selectedFiles]);

    return (
        <Box mt="20px" mb={5} width={width} {...ContainerProps}>
            {label && <CustomFormLabel>{label}</CustomFormLabel>}
            <Box
                mb={1}
                display={filesNamesInline ? 'block' : 'flex'}
                alignItems="center"
                width="100%"
            >
                {files.map((file, i) => (
                    <Box
                        key={file.name + i}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        flex="0 1 100%"
                    >
                        <Typography
                            key={file.name}
                            className={clsx(
                                classes.fileName,
                                isTableCell && classes.tableCellFileName
                            )}
                            title={`${texts.UPLOADED_FILE_NAME} : ${file.name || texts.AUCUN}`}
                            variant="body2"
                            noWrap
                        >{`${texts.UPLOADED_FILE_NAME} : ${file.name || texts.AUCUN}`}</Typography>
                        {!!file.name && (
                            <IconButton size="small" id={i} onClick={removeFile}>
                                <icons.Delete />
                            </IconButton>
                        )}
                    </Box>
                ))}
                {inline && (
                    <FileInputButton
                        className={isTableCell ? classes.fileInputButton : null}
                        noMargin
                        acceptedFileTypes={acceptedFileTypes}
                        showIcon
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleInterceptChange}
                        id={id}
                    />
                )}
            </Box>
            {fileSizeError && <FileErrorMessage variant="size" useMargin={false} />}
            {fileTypeError && <FileErrorMessage variant="type" useMargin={false} />}
            <ErrorMessage gutterBottom message={error} />
            <ErrorMessage gutterBottom message={pdfDocError ? texts.DOC_MUST_BE_PDF : null} />
            {!inline && (
                <FileInputButton
                    className={isTableCell ? classes.fileInputButton : null}
                    acceptedFileTypes={acceptedFileTypes}
                    noMargin
                    showIcon
                    label={texts.UPLOAD_FILE_BUTTON}
                    onFileChange={handleInterceptChange}
                    id={id}
                />
            )}
        </Box>
    );
};

DocumentUpload.propTypes = {
    type: PropTypes.oneOf(['pdf', 'image', 'all']),
    id: PropTypes.string,
    inline: PropTypes.bool,
    isTableCell: PropTypes.bool,
    selectedFiles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    removeFile: PropTypes.func,
    ContainerProps: PropTypes.object,
    handleFileChange: PropTypes.func,
    label: PropTypes.string,
    filesNamesInline: PropTypes.bool,
    error: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    acceptedFileTypes: PropTypes.string,
};

export default DocumentUpload;
